import React, { useCallback } from 'react'
import { useJoinFunnelAnalytics } from '@components/Analytics/useJoinFunnelAnalytics'
import { usePrivy } from '@towns/privy'
import { Box, FancyButton } from '@ui'
import { useCombinedAuth } from 'privy/useCombinedAuth'
import type { FancyButtonProps } from 'ui/components/Button/FancyButton'
import { AuthStatus, useConnectivity } from 'use-towns-client'

type LoginComponentProps = {
    text?: React.ReactNode
    loggingInText?: string
    onLoginClick?: () => void
} & FancyButtonProps

function LoginComponent({
    text = 'Login',
    loggingInText = 'Logging In...',
    onLoginClick: onLoginClick,
    ...props
}: LoginComponentProps) {
    const { ready: privyReady } = usePrivy()
    const { login, isAutoLoggingInToRiver } = useCombinedAuth()
    const { authStatus: libAuthStatus } = useConnectivity()
    const { privyModalShown } = useJoinFunnelAnalytics()

    const isBusy = libAuthStatus === AuthStatus.EvaluatingCredentials || isAutoLoggingInToRiver

    const loginContent = () => {
        if (isAutoLoggingInToRiver) {
            return loggingInText
        }
        // on app load user starts in a logging in state.
        // The goal is to never show this button in that state.
        // but in case this component appears, just let the spinner show
        if (AuthStatus.EvaluatingCredentials === libAuthStatus) {
            return ''
        }
        return text
    }

    const onButtonClick = useCallback(async () => {
        if (!privyReady || isBusy) {
            return
        }
        onLoginClick?.()
        privyModalShown()
        await login()
    }, [privyReady, isBusy, privyModalShown, onLoginClick, login])

    if (AuthStatus.EvaluatingCredentials === libAuthStatus) {
        return <></>
    }

    return (
        <Box centerContent gap="lg" width={props.width}>
            <Box width="100%">
                <FancyButton
                    cta
                    disabled={!privyReady || isBusy}
                    spinner={isBusy}
                    // eslint-disable-next-line @typescript-eslint/no-base-to-string
                    data-testid={`${text?.toString().toLowerCase()}-button`}
                    onClick={onButtonClick}
                    {...props}
                >
                    {loginContent() as string}
                </FancyButton>
            </Box>
        </Box>
    )
}

export default LoginComponent
